import { useState } from "react";
import axios from "axios";
import { AnimatePresence, motion } from "framer-motion";
import { toast } from "sonner";
import { X, Minus, Plus, Trash2, ShoppingBag, Loader2 } from "lucide-react";
import { useCart } from "@/components/CartContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt = (cents) =>
  new Intl.NumberFormat("en-CA", { style: "currency", currency: "CAD" }).format(cents / 100);

export default function CartDrawer() {
  const { items, remove, setQty, total, open, setOpen } = useCart();
  const [loading, setLoading] = useState(false);

  const checkout = async () => {
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/payments/checkout`, {
        items: items.map((i) => ({ lookup_key: i.lookup_key, quantity: i.quantity })),
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Checkout failed to start. Please try again.");
      setLoading(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setOpen(false)}
            data-testid="cart-overlay"
            className="fixed inset-0 z-50 bg-[#0A0A0A]/50"
          />
          <motion.aside
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ type: "spring", damping: 30, stiffness: 300 }}
            data-testid="cart-drawer"
            className="fixed right-0 top-0 z-50 flex h-full w-full max-w-md flex-col border-l-2 border-[#0A0A0A] bg-[#FDFBF7]"
          >
            <div className="flex items-center justify-between border-b-2 border-[#0A0A0A] bg-[#FFD700] px-6 py-4">
              <h2 className="font-display text-xl font-extrabold uppercase tracking-tight">Your cart</h2>
              <button onClick={() => setOpen(false)} data-testid="cart-close-button" aria-label="Close cart"
                className="rounded-full border-2 border-[#0A0A0A] bg-white p-1.5 hover:bg-[#FF2A54] hover:text-white">
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto px-6 py-6">
              {items.length === 0 ? (
                <div data-testid="cart-empty" className="flex h-full flex-col items-center justify-center text-center">
                  <ShoppingBag className="h-12 w-12 text-[#FF2A54]" />
                  <p className="mt-4 font-display text-lg font-extrabold uppercase">Nothing sweet yet</p>
                  <p className="mt-1 text-sm text-[#4A4A4A]">Add some candy or toys to get started.</p>
                </div>
              ) : (
                <ul className="flex flex-col gap-4">
                  {items.map((i) => (
                    <li key={i.lookup_key} data-testid={`cart-item-${i.slug}`}
                      className="hard-shadow flex gap-4 rounded-2xl border-2 border-[#0A0A0A] bg-white p-3">
                      <img src={i.image} alt={i.name} className="h-20 w-20 rounded-xl border-2 border-[#0A0A0A] object-cover" />
                      <div className="flex flex-1 flex-col">
                        <div className="flex items-start justify-between gap-2">
                          <h3 className="font-display text-sm font-bold tracking-tight">{i.name}</h3>
                          <button onClick={() => remove(i.lookup_key)} data-testid={`cart-remove-${i.slug}`} aria-label={`Remove ${i.name}`}
                            className="text-[#4A4A4A] hover:text-[#FF2A54]">
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                        <p className="text-xs text-[#4A4A4A]">{fmt(i.price_cents)} each</p>
                        <div className="mt-auto flex items-center justify-between pt-2">
                          <div className="flex items-center gap-2">
                            <button onClick={() => setQty(i.lookup_key, i.quantity - 1)} data-testid={`cart-qty-minus-${i.slug}`}
                              aria-label="Decrease quantity"
                              className="rounded-full border-2 border-[#0A0A0A] p-1 hover:bg-[#FFD700]">
                              <Minus className="h-3 w-3" />
                            </button>
                            <span data-testid={`cart-qty-${i.slug}`} className="w-6 text-center font-display text-sm font-extrabold">{i.quantity}</span>
                            <button onClick={() => setQty(i.lookup_key, i.quantity + 1)} data-testid={`cart-qty-plus-${i.slug}`}
                              aria-label="Increase quantity" disabled={i.quantity >= i.stock}
                              className="rounded-full border-2 border-[#0A0A0A] p-1 hover:bg-[#FFD700] disabled:opacity-40">
                              <Plus className="h-3 w-3" />
                            </button>
                          </div>
                          <span className="font-display text-sm font-extrabold">{fmt(i.price_cents * i.quantity)}</span>
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {items.length > 0 && (
              <div className="border-t-2 border-[#0A0A0A] bg-white px-6 py-5">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-bold uppercase tracking-widest text-[#4A4A4A]">Subtotal</span>
                  <span data-testid="cart-total" className="font-display text-2xl font-extrabold">{fmt(total)}</span>
                </div>
                <p className="mt-1 text-xs text-[#4A4A4A]">Tax calculated at checkout. Ships across Canada.</p>
                <button onClick={checkout} disabled={loading} data-testid="cart-checkout-button"
                  className="hard-shadow hard-shadow-hover mt-4 inline-flex w-full items-center justify-center gap-2 rounded-full border-2 border-[#0A0A0A] bg-[#FF2A54] px-8 py-4 font-display text-base font-extrabold uppercase text-white disabled:opacity-60">
                  {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : <ShoppingBag className="h-5 w-5" />}
                  {loading ? "One sec" : "Checkout"}
                </button>
              </div>
            )}
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
}
